import axios, { AxiosRequestConfig } from "axios";
import queryString from "qs";
import { useTranslation } from "react-i18next";
import { usePostHog } from "posthog-js/react";
// import * as Sentry from "@sentry/react";
import { useToast } from "@context/ToastContext";
import { trackApiFailure, getUserContext } from "../utils/posthogHelper";
const averageApiResponseTime = 1000;

const options: AxiosRequestConfig = {
  baseURL: import.meta.env.VITE_BASE_URL,
  paramsSerializer: {
    serialize: (params) => {
      return queryString.stringify(params, {
        skipNulls: true,
        arrayFormat: "repeat",
      });
    },
  },
};

const useApi = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const apiPublic = axios.create(options);
  const apiPrivate = axios.create(options);
  const { showToast } = useToast();
  const posthog = usePostHog();

  // --------------------------------------------------------------------------------------------------------
  // Carry the UI language on anonymous requests too. Without this the browser's
  // own Accept-Language (the OS locale) is what reaches the API, and endpoints
  // that pick an email language from that header — e.g. onboarding verify-email,
  // which sends the welcome email — ignore the language the host actually chose.
  // Set rather than replace the header bag, so per-request headers set by
  // callers (Content-Type on uploads, etc.) survive.
  apiPublic.interceptors.request.use(
    (config) => {
      config.headers.set("Accept-Language", language);

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor
  apiPublic.interceptors.response.use(
    (response) => {
      const { config } = response;
      const endTime = new Date();
      const startTime = config.metadata?.startTime || new Date();
      const duration = endTime.getTime() - startTime.getTime();
      if (duration > averageApiResponseTime) {
        // sentryApiLogger(
        //   config.url,
        //   config.method,
        //   response.status,
        //   config.data,
        //   response.data,
        //   duration
        // );
      }
      return response;
    },
    (error) => {
      const { config, response } = error;
      const endTime = new Date();
      const startTime = config?.metadata?.startTime || new Date();
      const duration = endTime.getTime() - startTime.getTime();

      const errorMessages = response?.data?.errorMessages;
      const errorMessage =
        language === "ar"
          ? errorMessages?.[0]?.messageAr
          : errorMessages?.[0]?.messageEn;
      
      // Track API failure in PostHog
      trackApiFailure(posthog, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        statusCode: response?.status,
        errorMessage: errorMessage,
        errorMessages: errorMessages,
        responseTime: duration,
      });

      if (errorMessage) {
        if (
          config?.url == "/api/Gathren_Listing" ||
          config?.url == "api/Airbnb_Listing"
        ) {
          return;
        }
        // Opt-out: callers that render the error inline (e.g. MS-490's
        // resume-session exchange, which shows its own bilingual notice
        // instead) pass `skipErrorToast` to avoid a duplicate toast. Mirrors
        // the same opt-out already honoured below on apiPrivate.
        if (!(config as any)?.skipErrorToast) {
          showToast(errorMessage);
        }
      }

      if (
        response?.headers["content-type"]?.includes("text/html") &&
        response.data.toLowerCase().includes("support id")
      ) {
        const ticketNumber = response.data.replace(/\D+/g, "");
        alert(
          `The requested URL was rejected. Please consult with your administrator. Your support ID is: ${ticketNumber}`
        );
      }

      return Promise.reject(error);
    }
  );

  // Add a request interceptor
  apiPrivate.interceptors.request.use(
    async (config: any) => {
      config.headers = {
        "Accept-Language": language,
        Authorization: "Bearer " + window.localStorage.token,
      };

      // Record the request start time
      config.metadata = { startTime: new Date() };

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor
  apiPrivate.interceptors.response.use(
    (response) => {
      const { config } = response;
      const endTime = new Date();
      const startTime = config.metadata?.startTime || new Date();
      const duration = endTime.getTime() - startTime.getTime();
      if (duration > averageApiResponseTime) {
        // sentryApiLogger(
        //   config.url,
        //   config.method,
        //   response.status,
        //   config.data,
        //   response.data,
        //   duration
        // );
      }
      return response;
    },
    (error) => {
      const { config, response } = error;
      const endTime = new Date();
      const startTime = config?.metadata?.startTime || new Date();
      const duration = endTime.getTime() - startTime.getTime();

      const errorMessages = response?.data?.errorMessages;
      const errorMessage =
        language === "ar"
          ? errorMessages?.[0]?.messageAr
          : errorMessages?.[0]?.messageEn;
      
      // Track API failure in PostHog
      trackApiFailure(posthog, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        statusCode: response?.status,
        errorMessage: errorMessage,
        errorMessages: errorMessages,
        responseTime: duration,
      });

      if (errorMessage) {
        if (config?.url == "/api/Gathren_Listing") {
          return;
        }
        // Opt-out: callers that render the error inline (e.g. the onboarding
        // forgot-password email step) pass `skipErrorToast` to avoid a toast.
        if (!(config as any)?.skipErrorToast) {
          showToast(errorMessage);
        }
      }

      if (
        response?.headers["content-type"]?.includes("text/html") &&
        response.data.toLowerCase().includes("support id")
      ) {
        const ticketNumber = response.data.replace(/\D+/g, "");
        alert(
          `The requested URL was rejected. Please consult with your administrator. Your support ID is: ${ticketNumber}`
        );
      }

      return Promise.reject(error);
    }
  );

  return { apiPrivate, apiPublic };
};

export { useApi };

// export const sentryApiLogger = (
//   baseUrl = "",
//   method = "",
//   statusCode: number | string = "",
//   body = {},
//   response = {},
//   responseTime = 1000
// ) => {
//   Sentry.captureException(new Error(baseUrl + method), {
//     tags: {
//       apiEndpoint: baseUrl,
//       method,
//       statusCode: statusCode.toString(),
//     },
//     extra: {
//       body,
//       responsePayload: response,
//       responseTime,
//     },
//   });
// };
